import getProp from '@f/get-prop'
import { createSelector } from 'reselect'
import { getCurrentUser } from './current-user'
import { getSourceSettingsArray } from './sources'
import { orderBy } from 'lodash'

function toArray (data) {
  const arr = []

  for (const slug in data) {
    const metadata = data[slug].data
    if (metadata) arr.push(metadata)
  }

  return arr
}

function toMap (data, key) {
  const result = {}

  for (const slug in data) {
    const metadata = data[slug].data
    const k = metadata[key]
    if (metadata) result[k] = metadata
  }

  return result
}

const getMetadatasByType = (state, type) => getProp(`metadatasBySlug.${type}`, state) || {}

/*
 * State accessors
 * These should only select state
 */

const getIntegrationMetadatasState = (state) => getMetadatasByType(state, 'integration')
const getSourceMetadatasState = (state) => getMetadatasByType(state, 'source')
const getWarehouseMetadatasState = (state) => getMetadatasByType(state, 'warehouse')

/*
 * Derived state
 * The following should use reselect
 */

const createMetadatasArraySelector = selector => createSelector(
  selector,
  ({ data }) => orderBy(toArray(data), 'name')
)
export const getIntegrationMetadatasArray = createMetadatasArraySelector(getIntegrationMetadatasState)
export const getSourceMetadatasArray = createMetadatasArraySelector(getSourceMetadatasState)
export const getWarehouseMetadatasArray = createMetadatasArraySelector(getWarehouseMetadatasState)

const createMetadatasByKeySelector = (selector, key) => createSelector(
  selector,
  ({ data }) => toMap(data, key)
)
export const getIntegrationMetadatasByName = createMetadatasByKeySelector(getIntegrationMetadatasState, 'name')

export const getIntegrationMetadatasCategories = createSelector(
  getIntegrationMetadatasArray,
  (integrations) => integrations.reduce((memo, integration) => {
    for (const category of integration.categories) {
      if (!memo.includes(category)) memo.push(category)
    }
    return memo
  }, []).sort()
)

export const getVisibleIntegrationMetadatas = createSelector(
  [ getIntegrationMetadatasArray, getCurrentUser ],
  (integrations, { isAdmin }) => integrations.filter(x => x.public || isAdmin)
)

/*
 * Multi-argument selectors
 * The following selectors use one or more additional arguments besides `state`.
 * They therefore cannot be memoized to the same extent as the selectors above.
 */

const getMetadatasArrayByType = (state, type) => {
  switch (type) {
    case 'integration':
      return getIntegrationMetadatasArray(state)
    case 'source':
      return getSourceMetadatasArray(state)
    case 'warehouse':
      return getWarehouseMetadatasArray(state)
    default:
      // Unoptimized fallback
      const { data } = getMetadatasByType(state, type)
      return toArray(data)
  }
}

export function getMetadataByTypeAndSlug (state, type, slug) {
  // Metadata slugs can contain dots 😱
  const metadataType = getProp(`metadatasBySlug.${type}.data`, state)
  return metadataType && metadataType[slug]
}

export function getMetadataByTypeAndId (state, type, id) {
  const metadatas = getMetadatasByType(state, type).data || {}
  for (const slug in metadatas) {
    const metadata = metadatas[slug].data
    if (metadata.id === id) return metadata
  }
}

const getMetadataByTypeAndOwnerId = (state, type, ownerId) => {
  return getMetadatasArrayByType(state, type)
    .filter(item => item.owners && item.owners.indexOf(ownerId) !== -1)
}

export const getIntegrationMetadataByName = (state, name) => {
  return getIntegrationMetadatasArray(state).find(integration => integration.name === name)
}

export const getSourceMetadataSlugBySourceSettingsSlug = (state, workspaceSlug, sourceSettingSlug) => {
  const sourceSettings = getSourceSettingsArray(state, workspaceSlug)
  const source = sourceSettings.find(source => source.slug === sourceSettingSlug)
  const sourceId = getProp('sourceId', source)
  const sourceMetadata = getMetadataByTypeAndId(state, 'source', sourceId)
  if (sourceMetadata && sourceMetadata.slug) return sourceMetadata.slug
}

export const getWarehouseMetadataById = (state, id) => {
  return getWarehouseMetadatasArray(state)
    .find(item => item.id === id)
}

export const getAllMetadatasByOwnerId = (state, ownerId) => {
  const types = Object.keys(state.metadatasBySlug)
  const res = {}
  for (const type of types) {
    res[type] = getMetadataByTypeAndOwnerId(state, type, ownerId)
  }
  return res
}

export const getFirstMetadataByOwnerId = (state, ownerId) => {
  const obj = getAllMetadatasByOwnerId(state, ownerId)
  for (const type in obj) {
    if (obj[type].length) {
      return { type, metadata: obj[type][0] }
    }
  }
  return null
}

export const isUserAPartner = (state, userId) => {
  return !!getFirstMetadataByOwnerId(state, userId)
}

export const getMetadataRequest = (state, type) => {
  return getProp(`metadatasBySlug.${type}`, state) || {}
}

// Backwards compatibility

export const getIntegrationMetadatasRequest = (state) => (
  getIntegrationMetadatasState(state).isRequesting
)



// WEBPACK FOOTER //
// ./selectors/metadata.js